<template>
  <div v-if="loading" class="loading-state">
    <a-spin size="large" />
  </div>

  <div v-else-if="application" class="member-detail">
    <!-- Header -->
    <div class="page-header">
      <h1 class="page-title">Detail Pendaftaran Member Baru</h1>
      <div class="name-row">
        <h2 class="member-name">{{ application.namaLengkap }}</h2>
        <span
          class="status-badge"
          :style="{
            color: getStatusConfig(application.status).color,
            background: getStatusConfig(application.status).bg,
            border: `0.5px solid ${getStatusConfig(application.status).border}`,
          }"
        >
          {{ getStatusConfig(application.status).label }}
        </span>
      </div>
    </div>

    <!-- Catatan Revisi Card (shown when there's any revision history) -->
    <div
      v-if="revisionHistoryEntries.length"
      class="revision-card"
      :class="application.status === 'approved' ? 'revision-card--approved' : ''"
    >
      <div class="revision-card-header">
        <span class="revision-card-title">Catatan Revisi</span>
        <span v-if="application.status === 'approved'" class="revision-checkmark">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="10" fill="#2D9F3A"/>
            <path d="M5.5 10.5L8.5 13.5L14.5 7" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="revision-see-all" @click="showRevisionHistoryModal = true">Lihat semua</span>
      </div>
      <template v-if="application.status !== 'approved'">
        <div v-if="latestRevisionEntry" class="revision-entry">
          <div class="revision-timeline-row">
            <span class="revision-dot" />
            <span class="revision-timestamp">{{ formatDate(latestRevisionEntry.changedAt) }}</span>
          </div>
          <div class="revision-timeline-body">
            <div class="revision-line-wrapper">
              <div class="revision-line" />
            </div>
            <div class="revision-content">
              <div class="revision-notes-box">
                "{{ parseNotes(latestRevisionEntry.notes).catatan }}"
              </div>
              <div class="revision-tags">
                <span
                  v-for="section in parseNotes(latestRevisionEntry.notes).sections"
                  :key="section"
                  class="revision-tag revision-tag--clickable"
                  @click="navigateToSection(section)"
                >{{ section }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div v-if="latestRevisionEntry" class="revision-entry">
          <div class="revision-timestamp" style="margin-bottom: 6px">{{ formatDate(latestRevisionEntry.changedAt) }}</div>
          <div class="revision-notes-box revision-notes-box--approved">
            "{{ parseNotes(latestRevisionEntry.notes).catatan }}"
          </div>
          <div class="revision-tags" style="margin-top: 6px">
            <span
              v-for="section in parseNotes(latestRevisionEntry.notes).sections"
              :key="section"
              class="revision-tag revision-tag--approved revision-tag--clickable"
              @click="navigateToSection(section)"
            >{{ section }}</span>
          </div>
        </div>
      </template>
    </div>

    <!-- Tabs -->
    <div class="tabs-bar">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="tab-item"
        :class="{ active: activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-panel" style="margin-bottom: 0">
      <member-application-tab-data-diri
        v-if="activeTab === 'data-diri'"
        :data="application"
      />
      <member-application-tab-data-pendidikan
        v-else-if="activeTab === 'data-pendidikan'"
        :data="application"
      />
      <member-application-tab-data-pekerjaan
        v-else-if="activeTab === 'data-pekerjaan'"
        :data="application"
      />
      <member-application-tab-data-dokumen
        v-else-if="activeTab === 'data-dokumen'"
        :data="application"
      />
      <member-application-tab-berkas-pendukung
        v-else-if="activeTab === 'berkas-pendukung'"
        :data="application"
      />

      <!-- Footer Actions -->
      <div class="form-footer" :class="{ 'form-footer--last': activeTab === tabs[tabs.length - 1].key }">
        <button class="btn-outline" @click="handleBack">KEMBALI</button>
        <button
          v-if="activeTab !== tabs[tabs.length - 1].key"
          class="btn-primary"
          @click="goNext"
        >
          SELANJUTNYA
        </button>
        <!-- Last tab: action buttons (only for pending) -->
        <div v-else-if="application.status === 'pending'" class="submit-actions">
          <button class="btn-approve" @click="openApprove">
            TERIMA PENDAFTARAN
          </button>
          <button class="btn-revisi" @click="openRevisi">MINTA REVISI</button>
          <button class="btn-tolak" @click="openReject">TOLAK PENDAFTARAN</button>
        </div>
      </div>
    </div>

    <!-- Approve Modal -->
    <a-modal
      v-model:visible="showApproveModal"
      :footer="null"
      :closable="false"
      :centered="true"
      width="460px"
      :body-style="{ padding: 0 }"
      wrap-class-name="custom-modal-wrap"
    >
      <div class="custom-modal">
        <div class="custom-modal-header">
          <span class="custom-modal-title">Setujui member baru?</span>
          <button class="custom-modal-close" @click="showApproveModal = false">×</button>
        </div>
        <p class="custom-modal-desc">
          Mohon cek kembali data pendaftaran sebelum menyetujui. Member baru akan terdaftar secara resmi di sistem PERKI.
        </p>
        <label class="modal-checkbox-label">
          <input v-model="approveConfirmed" type="checkbox" class="modal-checkbox-input" />
          <span>Saya telah memastikan bahwa syarat pendaftaran sudah terpenuhi semua.</span>
        </label>
        <div class="custom-modal-footer">
          <button class="modal-btn-secondary" @click="showApproveModal = false">KEMBALI</button>
          <button
            class="modal-btn-primary"
            :class="{ disabled: !approveConfirmed }"
            :disabled="!approveConfirmed || actionLoading"
            @click="submitApprove"
          >
            {{ actionLoading ? 'MEMPROSES...' : 'YA, SETUJUI' }}
          </button>
        </div>
      </div>
    </a-modal>

    <!-- Tolak Modal -->
    <a-modal
      v-model:visible="showRejectModal"
      :footer="null"
      :closable="false"
      :centered="true"
      width="460px"
      :body-style="{ padding: 0 }"
      wrap-class-name="custom-modal-wrap"
    >
      <div class="custom-modal">
        <div class="custom-modal-header">
          <span class="custom-modal-title">Tolak Pendaftaran?</span>
          <button class="custom-modal-close" @click="showRejectModal = false">×</button>
        </div>
        <p class="custom-modal-desc">
          Apakah Anda yakin menolak <strong>{{ application && application.namaLengkap }}</strong> sebagai calon member PERKI? Mohon berikan alasan penolakan pada kolom di bawah.
        </p>
        <div class="custom-modal-field">
          <label class="custom-field-label">Alasan Penolakan <span class="required">*</span></label>
          <a-textarea
            v-model:value="rejectNotesInput"
            :rows="4"
            placeholder="Masukkan alasan penolakan"
          />
        </div>
        <div class="custom-modal-footer">
          <button class="modal-btn-secondary" @click="showRejectModal = false">KEMBALI</button>
          <button class="modal-btn-primary" :disabled="actionLoading" @click="submitReject">
            {{ actionLoading ? 'MEMPROSES...' : 'YA, KIRIM' }}
          </button>
        </div>
      </div>
    </a-modal>

    <!-- Revisi Modal -->
    <a-modal
      v-model:visible="showRevisiModal"
      :footer="null"
      :closable="false"
      :centered="true"
      width="460px"
      :body-style="{ padding: 0 }"
      wrap-class-name="custom-modal-wrap"
    >
      <div class="custom-modal">
        <div class="custom-modal-header">
          <span class="custom-modal-title">Minta calon member untuk revisi?</span>
          <button class="custom-modal-close" @click="showRevisiModal = false">×</button>
        </div>
        <p class="custom-modal-desc">
          Formulir akan dikembalikan ke calon member untuk direvisi kembali. Pilih bagian data yang perlu direvisi (bisa lebih dari satu) serta berikan alasannya.
        </p>
        <div class="custom-modal-field">
          <label class="custom-field-label">Bagian yang perlu direvisi <span class="required">*</span></label>
          <div class="revisi-select-wrapper">
            <div class="revisi-select-input" @click="revisiDropdownOpen = !revisiDropdownOpen">
              <span class="revisi-placeholder">Pilih bagian data yang perlu direvisi</span>
              <span class="revisi-chevron" :class="{ open: revisiDropdownOpen }" />
            </div>
            <div v-if="revisiDropdownOpen" class="revisi-backdrop" @click="revisiDropdownOpen = false" />
            <div v-if="revisiDropdownOpen" class="revisi-dropdown">
              <button
                v-for="opt in revisiOptions"
                :key="opt"
                class="revisi-dropdown-item"
                :class="{ selected: revisiSections.includes(opt) }"
                @click.stop="toggleRevisiSection(opt)"
              >
                {{ opt }}
              </button>
            </div>
          </div>
          <div v-if="revisiSections.length" class="revisi-tags">
            <span
              v-for="opt in revisiSections"
              :key="opt"
              class="revisi-tag"
            >
              {{ opt }}
              <button class="revisi-tag-remove" @click="toggleRevisiSection(opt)">×</button>
            </span>
          </div>
        </div>
        <div class="custom-modal-field">
          <label class="custom-field-label">Catatan Revisi <span class="required">*</span></label>
          <a-textarea
            v-model:value="revisiNotes"
            :rows="4"
            placeholder="Masukkan catatan revisi yang perlu diperbaiki calon member PERKI"
          />
        </div>
        <div class="custom-modal-footer">
          <button class="modal-btn-secondary" @click="showRevisiModal = false">KEMBALI</button>
          <button class="modal-btn-primary" :disabled="actionLoading" @click="submitRevisi">
            {{ actionLoading ? 'MEMPROSES...' : 'YA, KIRIM' }}
          </button>
        </div>
      </div>
    </a-modal>
    <!-- Revision History Modal -->
    <a-modal
      v-model:visible="showRevisionHistoryModal"
      :footer="null"
      :closable="false"
      :centered="true"
      width="560px"
      :body-style="{ padding: 0 }"
      wrap-class-name="custom-modal-wrap"
    >
      <div class="custom-modal revision-history-modal">
        <div class="custom-modal-header">
          <span class="custom-modal-title">Semua Catatan Revisi</span>
          <button class="custom-modal-close" @click="showRevisionHistoryModal = false">×</button>
        </div>
        <div class="revision-history-list">
          <div
            v-for="(entry, idx) in [...revisionHistoryEntries].reverse()"
            :key="entry.id || idx"
            class="revision-history-item"
          >
            <div class="revision-timeline-row">
              <span class="revision-dot" />
              <span class="revision-timestamp">{{ formatDate(entry.changedAt) }}</span>
            </div>
            <div class="revision-timeline-body">
              <div class="revision-line-wrapper">
                <div class="revision-line" />
              </div>
              <div class="revision-content">
                <div class="revision-history-card">
                  <div class="revision-card-title">Catatan Revisi</div>
                  <div class="revision-notes-box">
                    "{{ parseNotes(entry.notes).catatan }}"
                  </div>
                  <div class="revision-tags">
                    <span
                      v-for="section in parseNotes(entry.notes).sections"
                      :key="section"
                      class="revision-tag"
                    >{{ section }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMemberApplicationDetail } from './composables/useMemberApplicationDetail'
import MemberApplicationTabDataDiri from './MemberApplicationTabDataDiri.vue'
import MemberApplicationTabDataPendidikan from './MemberApplicationTabDataPendidikan.vue'
import MemberApplicationTabDataPekerjaan from './MemberApplicationTabDataPekerjaan.vue'
import MemberApplicationTabDataDokumen from './MemberApplicationTabDataDokumen.vue'
import MemberApplicationTabBerkasPendukung from './MemberApplicationTabBerkasPendukung.vue'

export default {
  name: 'MemberApplicationDetail',
  components: { MemberApplicationTabDataDiri, MemberApplicationTabDataPendidikan, MemberApplicationTabDataPekerjaan, MemberApplicationTabDataDokumen, MemberApplicationTabBerkasPendukung },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const composable = useMemberApplicationDetail(route.params.id)

    onMounted(composable.loadApplication)

    const handleBack = () => {
      const idx = composable.tabs.findIndex(t => t.key === composable.activeTab.value)
      if (idx === 0) {
        router.push('/mobile-cms/member-application')
      } else {
        composable.goPrev()
      }
    }

    const showRevisionHistoryModal = ref(false)

    return { ...composable, handleBack, showRevisionHistoryModal }
  },
  computed: {
    revisionHistoryEntries() {
      return (this.applicationHistory || []).filter(h => h.status === 'revision')
    },
    latestRevisionEntry() {
      const entries = this.revisionHistoryEntries
      return entries.length ? entries[entries.length - 1] : null
    },
  },
  methods: {
    parseNotes(notes) {
      if (!notes) return { sections: [], catatan: '' }
      if (!notes.startsWith('Bagian:')) return { sections: [], catatan: notes }
      const parts = notes.split('\n\n')
      const sections = parts[0].replace('Bagian:', '').trim().split(', ').map(s => s.trim()).filter(Boolean)
      const catatan = parts.slice(1).join('\n\n').trim()
      return { sections, catatan }
    },
    navigateToSection(section) {
      const map = {
        'Berkas Pendukung': 'berkas-pendukung',
        'Dokumen': 'berkas-pendukung',
        'Data Diri': 'data-diri',
        'Data Pendidikan': 'data-pendidikan',
        'Data Pekerjaan': 'data-pekerjaan',
      }
      const key = Object.keys(map).find(k => section.toLowerCase().includes(k.toLowerCase()))
      if (key) this.activeTab.value = map[key]
    },
    formatDate(iso) {
      if (!iso) return '-'
      try {
        const dt = new Date(iso)
        const months = ['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des']
        const h = String(dt.getHours()).padStart(2, '0')
        const m = String(dt.getMinutes()).padStart(2, '0')
        return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()} ${h}:${m}`
      } catch { return iso }
    },
  },
}
</script>

<style>
.custom-modal-wrap .ant-modal-content {
  border-radius: 16px;
  padding: 0;
  overflow: visible;
  box-shadow: none;
}
.custom-modal-wrap .ant-modal-body {
  padding: 0;
}
</style>

<style scoped>
.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}

.member-detail {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.page-title {
  font-size: 30px;
  font-weight: 700;
  color: #282828;
  margin: 0 0 8px 0;
}

.name-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.member-name {
  font-size: 24px;
  font-weight: 700;
  color: #282828;
  margin: 0;
}

.status-badge {
  padding: 0 8px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 700;
  line-height: 22px;
  white-space: nowrap;
}

.tabs-bar {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0;
}

.tab-item {
  padding: 12px 24px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-size: 14px;
  color: #0b3334;
  white-space: nowrap;
  transition: color 0.2s;
}

.tab-item.active {
  color: #e10f28;
  border-bottom-color: #e10f28;
  font-weight: 700;
}

.tab-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.coming-soon {
  color: #798892;
  font-size: 14px;
  padding: 32px 0;
  text-align: center;
}

.form-footer {
  border-top: 1px solid #efefff;
  padding-top: 24px;
  display: flex;
  gap: 8px;
}

.form-footer--last {
  justify-content: space-between;
  align-items: center;
}

.btn-outline {
  padding: 8px 16px;
  border-radius: 50px;
  border: 2px solid #e10f28;
  background: #fff;
  color: #e10f28;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  min-height: 40px;
}

.btn-primary {
  padding: 8px 16px;
  border-radius: 50px;
  border: 2px solid #e10f28;
  background: #e10f28;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  min-height: 40px;
}

.submit-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.btn-approve {
  padding: 8px 16px;
  border-radius: 50px;
  border: 2px solid #e10f28;
  background: #e10f28;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  min-height: 40px;
}

.btn-revisi {
  padding: 8px 16px;
  border-radius: 50px;
  border: 2px solid #e10f28;
  background: #fff;
  color: #e10f28;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  min-height: 40px;
}

.btn-tolak {
  padding: 8px 16px;
  border-radius: 50px;
  border: 2px solid #282828;
  background: #fff;
  color: #282828;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  min-height: 40px;
}

/* Custom Modal */
.custom-modal {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.08);
}

.custom-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.custom-modal-title {
  font-size: 20px;
  font-weight: 700;
  color: #062d4b;
  line-height: 28px;
  flex: 1;
}

.custom-modal-close {
  background: none;
  border: none;
  font-size: 20px;
  color: #444;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-modal-desc {
  font-size: 14px;
  color: #062d4b;
  line-height: 22px;
  margin: 0;
}

.custom-modal-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.custom-field-label {
  font-size: 14px;
  font-weight: 700;
  color: #444;
}

.required {
  color: #dc3545;
}

.modal-checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  color: #444;
  line-height: 22px;
  cursor: pointer;
}

.modal-checkbox-input {
  margin-top: 3px;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
  border: 1px solid #d0d5dd;
  border-radius: 4px;
}

.custom-modal-footer {
  display: flex;
  gap: 8px;
}

.modal-btn-secondary {
  flex: 1;
  padding: 8px 16px;
  border-radius: 50px;
  border: 2px solid #282828;
  background: #fff;
  color: #282828;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  min-height: 40px;
}

.modal-btn-primary {
  flex: 1;
  padding: 8px 16px;
  border-radius: 50px;
  border: 2px solid #e10f28;
  background: #e10f28;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  min-height: 40px;
}

.modal-btn-primary.disabled,
.modal-btn-primary:disabled {
  border-color: #98a2b3;
  background: #98a2b3;
  cursor: not-allowed;
}

/* Revisi dropdown select */
.revisi-select-wrapper {
  position: relative;
  width: 100%;
}

.revisi-select-input {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #e4e9f0;
  border-radius: 6px;
  padding: 4px 16px;
  min-height: 32px;
  cursor: pointer;
  font-size: 14px;
  color: #798892;
}

.revisi-chevron {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-right: 2px solid #798892;
  border-bottom: 2px solid #798892;
  transform: rotate(45deg);
  transition: transform 0.2s;
  flex-shrink: 0;
  margin-bottom: 3px;
}

.revisi-chevron.open {
  transform: rotate(225deg);
  margin-bottom: -3px;
}

.revisi-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99;
}

.revisi-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e4e9f0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  max-height: 220px;
  overflow-y: auto;
}

.revisi-dropdown-item {
  display: block;
  width: 100%;
  padding: 8px 16px;
  background: none;
  border: none;
  text-align: left;
  font-size: 14px;
  color: #444;
  cursor: pointer;
  transition: background 0.15s;
}

.revisi-dropdown-item:hover {
  background: #f5f5f5;
}

.revisi-dropdown-item.selected {
  background: #efefff;
  color: #183e6f;
}

/* Revisi tags */
.revisi-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.revisi-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #efefff;
  border: 0.5px solid #8688ca;
  border-radius: 50px;
  padding: 4px 16px;
  font-size: 14px;
  color: #183e6f;
  white-space: nowrap;
}

.revisi-tag-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: #183e6f;
  font-size: 16px;
  line-height: 1;
  padding: 0;
  margin-left: 2px;
}

/* Revision Card */
.revision-card {
  background: #efefff;
  border: 1px solid #e4e9f0;
  border-radius: 6px;
  padding: 8px;
  box-shadow: 0px 4px 10px rgba(36, 81, 140, 0.08);
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 0;
}

.revision-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.revision-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #444;
}

.revision-timeline-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.revision-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #428a94;
  flex-shrink: 0;
}

.revision-timestamp {
  font-size: 14px;
  color: #444;
  line-height: 22px;
}

.revision-timeline-body {
  display: flex;
  gap: 12px;
  align-items: stretch;
  padding-left: 4px;
}

.revision-line-wrapper {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  width: 1px;
}

.revision-line {
  width: 1px;
  background: #428a94;
  flex: 1;
}

.revision-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 12px;
  min-width: 0;
}

.revision-notes-box {
  background: #fff;
  padding: 8px;
  font-size: 14px;
  color: #444;
  line-height: 22px;
}

.revision-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.revision-tag {
  background: #fff;
  border: 0.5px solid #444;
  border-radius: 50px;
  padding: 4px 16px;
  font-size: 14px;
  color: #183e6f;
  line-height: 22px;
  white-space: nowrap;
}

.revision-tag--clickable {
  cursor: pointer;
  transition: opacity 0.15s;
}

.revision-tag--clickable:hover {
  opacity: 0.75;
}

.revision-card--approved {
  background: #cdf3ce;
}

.revision-card--approved .revision-notes-box--approved {
  background: #fff;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  color: #444;
  line-height: 22px;
}

.revision-checkmark {
  display: flex;
  align-items: center;
}

.revision-tag--approved {
  background: #fff;
  border: 0.5px solid #2d9f3a;
  color: #1a5e22;
}

.revision-see-all {
  font-size: 14px;
  color: #e10f28;
  cursor: pointer;
  line-height: 22px;
  white-space: nowrap;
}

.revision-see-all:hover {
  opacity: 0.8;
}

.revision-history-modal {
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.revision-history-list {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.revision-history-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.revision-history-card {
  background: #efefff;
  border: 1px solid #e4e9f0;
  border-radius: 6px;
  padding: 8px;
  box-shadow: 0px 4px 10px rgba(36, 81, 140, 0.08);
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

:deep(.ant-input::placeholder),
:deep(textarea.ant-input::placeholder),
:deep(.ant-input-textarea textarea::placeholder) {
  color: #9ca3af;
}
</style>
